import {
	FileId,
	type FinancialPeriodPreparationMethod,
	type IncomeStatementId,
	type ISODate,
	type ISODateTime,
	type PeriodType,
	type SuretyAccountId,
} from "@inrev/common";
import { useFilePreview } from "@portal/api/files";
import {
	useAdminDeleteIncomeStatement,
	useAdminMarkIncomeStatementReviewed,
	useAdminMarkIncomeStatementUnreviewed,
	useAdminPromoteIncomeStatement,
	useAdminUpdateIncomeStatementPeriod,
} from "@portal/api/financials/admin";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { FC, useState } from "react";
import { useGetFileUrl } from "../../api";
import { PeriodDeleteModal } from "../ui/Financials/PeriodDeleteModal";
import { PeriodHeaderActionRow } from "../ui/Financials/PeriodHeaderActionRow";
import { PeriodReviewedModal } from "../ui/Financials/PeriodReviewedModal";
import { MutationErrorFlag } from "../ui/MutationErrorFlag";
import { PDFViewerModal } from "../ui/PDFViewerModal";
import { CreateOrUpdatePeriodModal } from "./CreateOrUpdatePeriodModal";

export type IncomeStatementPeriodHeaderContextMenuProps = {
	accountId: SuretyAccountId;
	incomeStatementData: {
		id: IncomeStatementId;
		fileId?: FileId;
		reviewedAt?: ISODateTime;
		periodEndDate?: ISODate;
		periodType?: PeriodType;
		preparationMethod?: FinancialPeriodPreparationMethod;
	};
	isActive?: boolean;
};

export const IncomeStatementPeriodHeaderContextMenu: FC<
	IncomeStatementPeriodHeaderContextMenuProps
> = ({ accountId, incomeStatementData, isActive }) => {
	const { isAdmin } = useAuthenticatedUser();
	const [showReviewedModal, setShowReviewedModal] = useState(false);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const { pdfViewerControl, openFilePreview, closeFilePreview } = useFilePreview(
		incomeStatementData.fileId,
	);
	const { showErrorFlag } = useFlags();
	const { reviewIncomeStatement, reviewISLoading, reviewISError, resetReviewISError } =
		useAdminMarkIncomeStatementReviewed(accountId);
	const { unreviewIncomeStatement, unreviewISLoading, unreviewISError, resetUnreviewISError } =
		useAdminMarkIncomeStatementUnreviewed(accountId);
	const { promoteIncomeStatement, promoteISLoading, promoteISError, resetPromoteISError } =
		useAdminPromoteIncomeStatement(accountId);
	const { deleteIncomeStatement, deleteISError, resetDeleteISError } =
		useAdminDeleteIncomeStatement(accountId);
	const {
		updateIncomeStatementPeriod,
		updateISPeriodLoading,
		updateISPeriodError,
		resetUpdateISPeriodError,
	} = useAdminUpdateIncomeStatementPeriod(accountId);
	const { getFileUrl, fileUrlError, fileUrlLoading, resetFileUrlError } = useGetFileUrl();
	const isReviewed = !!incomeStatementData.reviewedAt;
	const hasFile = !!incomeStatementData.fileId;

	const handleDownload = () => {
		if (fileUrlLoading) return;
		if (!incomeStatementData.fileId) {
			showErrorFlag({ heading: "Download Error", message: "No file associated with this period." });
			return;
		}
		getFileUrl({
			baseUrl: `/v2/files/${incomeStatementData.fileId}`,
			queryKey: ["periodFile", incomeStatementData.fileId],
			asDownload: true,
		});
	};

	const handlePreview = () => {
		if (!incomeStatementData.fileId) {
			showErrorFlag({ heading: "Preview Error", message: "No file associated with this period." });
			return;
		}
		openFilePreview();
	};

	const handleReview = () => reviewIncomeStatement(incomeStatementData.id);
	const handleUnreview = () => unreviewIncomeStatement(incomeStatementData.id);

	return (
		<div
			data-testid="income-statement-period-header-context-menu"
			className="absolute top-8 left-0 bg-surface-base border border-bc-default rounded w-max"
		>
			<div className="flex flex-col">
				{hasFile && (
					<>
						<PeriodHeaderActionRow
							testId="download-is-file-button"
							label="Download"
							iconType="arrow-down-tray"
							onClick={handleDownload}
							loading={fileUrlLoading}
						/>
						<PeriodHeaderActionRow
							testId="preview-is-file-button"
							label="Preview"
							iconType="eye"
							onClick={handlePreview}
						/>
					</>
				)}
				{isAdmin && (
					<>
						{isReviewed ? (
							<PeriodHeaderActionRow
								testId="unreview-is-button"
								label="Unmark Reviewed"
								iconType="document-text"
								onClick={() => setShowReviewedModal(true)}
								loading={unreviewISLoading}
							/>
						) : (
							<PeriodHeaderActionRow
								testId="review-is-button"
								label="Mark Reviewed"
								iconType="document-check"
								onClick={() => setShowReviewedModal(true)}
								loading={reviewISLoading}
							/>
						)}
						{!isActive && (
							<PeriodHeaderActionRow
								testId="promote-is-button"
								label="Promote"
								iconType="star"
								onClick={() => promoteIncomeStatement(incomeStatementData.id)}
								loading={promoteISLoading}
							/>
						)}
						<PeriodHeaderActionRow
							testId="edit-is-button"
							label="Edit"
							iconType="pencil-square"
							onClick={() => setShowEditModal(true)}
							loading={updateISPeriodLoading}
						/>
						<PeriodHeaderActionRow
							testId="delete-is-button"
							label="Delete"
							iconType="trash"
							onClick={() => setShowDeleteConfirmation(true)}
						/>
					</>
				)}
			</div>
			{showReviewedModal && (
				<PeriodReviewedModal
					reviewFn={isReviewed ? undefined : handleReview}
					unreviewFn={isReviewed ? handleUnreview : undefined}
					onClose={() => setShowReviewedModal(false)}
				/>
			)}
			{showDeleteConfirmation && (
				<PeriodDeleteModal
					deleteFn={() => deleteIncomeStatement(incomeStatementData.id)}
					onClose={() => setShowDeleteConfirmation(false)}
				/>
			)}
			{showEditModal && (
				<CreateOrUpdatePeriodModal
					accountId={accountId}
					onClose={() => setShowEditModal(false)}
					editConfig={{
						header: "Edit Income Statement Period",
						submitLabel: "Save",
						isLoading: updateISPeriodLoading,
						error: updateISPeriodError,
						dismissError: resetUpdateISPeriodError,
						initialValues: {
							periodEndDate: incomeStatementData.periodEndDate,
							periodType: incomeStatementData.periodType,
							preparationMethod: incomeStatementData.preparationMethod,
						},
						submit: (body, { onSuccess }) =>
							updateIncomeStatementPeriod(
								{ incomeStatementId: incomeStatementData.id, body },
								{ onSuccess },
							),
					}}
				/>
			)}
			<PDFViewerModal
				control={pdfViewerControl}
				onClose={closeFilePreview}
				trackingId="income-statement-source-viewer"
				header={`Income Statement${incomeStatementData.periodEndDate ? ` - ${incomeStatementData.periodEndDate}` : ""}`}
			/>
			<MutationErrorFlag
				errorHeading="Mark Reviewed Error"
				mutationError={reviewISError}
				dismissError={resetReviewISError}
			/>
			<MutationErrorFlag
				errorHeading="Unmark Reviewed Error"
				mutationError={unreviewISError}
				dismissError={resetUnreviewISError}
			/>
			<MutationErrorFlag
				errorHeading="Download Error"
				mutationError={fileUrlError}
				dismissError={resetFileUrlError}
			/>
			<MutationErrorFlag
				errorHeading="Promote Error"
				mutationError={promoteISError}
				dismissError={resetPromoteISError}
			/>
			<MutationErrorFlag
				errorHeading="Delete Error"
				mutationError={deleteISError}
				dismissError={resetDeleteISError}
			/>
		</div>
	);
};
